"use client";

import { useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Section } from "@marketing-sites/ui/primitives";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";
import RoundedCheck from "@/icons/rounded-check.svg";
import Close from "@/icons/close.svg";

const ANALYTICS_REGION = "Comparison Table";

export interface ComparisonColumn {
  /** Column header, e.g. "Intercom" or "Zendesk". Also used as the mobile tab label. */
  label: string;
  analyticsId: string;
  /** Styles this column as the "our product" column (green tick, full-strength text). Other columns get a muted cross. */
  highlighted?: boolean;
}

export interface ComparisonRow {
  /** Row label shown in the left-hand column, e.g. "AI Agent". */
  category: string;
  /**
   * One cell per column, index-aligned with `columns`. Pass `true`/`false` to
   * render a tick/cross only (no text) — the tick-or-cross matrix used on the
   * vs-competitor pages. Pass a string/element to render text alongside the
   * column's default tick (highlighted) or cross.
   */
  values: ReactNode[];
}

interface ComparisonTableBaseProps {
  id?: string;
  badge?: string;
  title: ReactNode;
  description?: ReactNode;
  columns: ComparisonColumn[];
  rows: ComparisonRow[];
  sectionMetadata: { analyticsId: string };
  dotDecorators?: boolean;
  /**
   * Force every column to render on mobile instead of the one-column-at-a-time
   * tab switcher. Defaults to auto: all columns when every cell is boolean
   * (icon-only), tabs when any cell has text.
   */
  showAllColumnsOnMobile?: boolean;
}

type ComparisonTableCollapsibleProps =
  | {
      initialVisibleRows?: undefined;
      viewMoreMetadata?: undefined;
      viewLessMetadata?: undefined;
    }
  | {
      /**
       * Collapse the table to the first N rows behind a "View more" toggle
       * (expandable and re-collapsible). Omit to always show every row. Collapsed
       * rows are CSS-hidden, not unmounted, so they stay in the server HTML.
       */
      initialVisibleRows: number;
      viewMoreMetadata: { analyticsId: string };
      viewLessMetadata: { analyticsId: string };
    };

export type ComparisonTableProps = ComparisonTableBaseProps &
  ComparisonTableCollapsibleProps;

/**
 * Head-to-head feature comparison table, rendered via the ProductPageTemplate's
 * `custom` section (compare-intercom-vs-zendesk, live-chat, conversational-ai).
 *
 * Compact (icon-only / boolean) tables show every column at all breakpoints —
 * the tick/cross columns are narrow enough to fit on mobile, like the live page.
 * Text-heavy tables fall back on mobile to a one-column-at-a-time tab switcher
 * (animated underline) — the same responsive pattern as fin-ai's DRLP comparison
 * table, rebuilt i2y4-native (Section primitive, typography utilities,
 * `text-current/opacity` + `border-current` tokens).
 */
export default function ComparisonTable({
  id = "comparison-table",
  badge,
  title,
  description,
  columns,
  rows,
  sectionMetadata,
  dotDecorators = true,
  initialVisibleRows,
  viewMoreMetadata,
  viewLessMetadata,
  showAllColumnsOnMobile,
}: ComparisonTableProps) {
  const [visibleColumn, setVisibleColumn] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const columnWidth = 100 / columns.length;
  // Label column is a fixed 40%; data columns share the remaining 60% so the
  // row always sums to 100% for any column count (30% each at 2 columns).
  const dataColWidth = 60 / columns.length;

  // Icon-only (tick/cross) tables are narrow enough to show every column at all
  // breakpoints — the live-page layout. Text tables fall back to the mobile
  // one-column-at-a-time tab switcher unless the caller opts out.
  const compact =
    showAllColumnsOnMobile ??
    rows.every((row) =>
      row.values.every((value) => typeof value === "boolean"),
    );

  const visibleCount =
    expanded || initialVisibleRows == null ? rows.length : initialVisibleRows;

  return (
    <Section
      id={id}
      data-analytics-region={ANALYTICS_REGION}
      data-analytics-id={sectionMetadata.analyticsId}
      gridCols="10"
      className="py-16 md:py-20"
      dotDecorators={dotDecorators}
    >
      <div className="col-span-full space-y-8 md:space-y-12">
        <div className="space-y-6 lg:max-w-[60%]">
          {badge && <span className="text-label-lg block">[ {badge} ]</span>}
          {title && <h2 className="text-heading-3 text-balance">{title}</h2>}
          {description && (
            <p className="text-body-lg text-current/80 text-pretty">
              {description}
            </p>
          )}
        </div>

        <div>
          {/* Mobile column switcher — desktop shows all columns at once.
              Hidden entirely for compact (icon-only) tables, which show every
              column on mobile too. */}
          <div
            className={cn("relative mb-2 flex md:hidden", compact && "hidden")}
          >
            {columns.map((column, index) => (
              <button
                type="button"
                key={column.label}
                aria-pressed={index === visibleColumn}
                onClick={() => {
                  setVisibleColumn(index);
                  triggerAnalyticsClick({
                    text: column.label,
                    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the call site
                    analyticsId: column.analyticsId,
                    location: ANALYTICS_REGION,
                  });
                }}
                className={cn(
                  "text-label-lg border-current/20 flex-1 cursor-pointer border-b py-3 text-center transition-colors",
                  index === visibleColumn ? "text-current" : "text-current/40",
                )}
              >
                {column.label}
              </button>
            ))}
            <span
              aria-hidden
              className="bg-helpdesk absolute bottom-0 h-px transition-[left] duration-300 ease-in-out"
              style={{
                width: `${columnWidth}%`,
                left: `${columnWidth * visibleColumn}%`,
              }}
            />
          </div>

          <table
            className="w-full border-separate border-spacing-0"
            style={{ "--data-col": `${dataColWidth}%` } as CSSProperties}
          >
            <thead>
              <tr>
                <th
                  className={cn(
                    "border-current/10 border-b py-3 md:w-[40%]",
                    compact ? "w-[40%]" : "hidden md:table-cell",
                  )}
                />
                {columns.map((column) => (
                  <th
                    key={column.label}
                    className={cn(
                      "text-label-lg border-current/10 md:w-(--data-col) border-b py-3 text-center font-normal",
                      compact ? "w-(--data-col)" : "hidden md:table-cell",
                      column.highlighted ? "text-current" : "text-current/40",
                    )}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={row.category}
                  className={cn(rowIndex >= visibleCount && "hidden")}
                >
                  <td
                    className={cn(
                      "text-body md:text-body-lg border-current/10 h-24 border-b py-6 pr-4 align-middle text-current md:w-[40%] md:pr-6",
                      compact && "w-[40%]",
                    )}
                  >
                    {row.category}
                  </td>
                  {row.values.map((value, columnIndex) => {
                    const iconOnly = typeof value === "boolean";
                    const showTick = iconOnly
                      ? value
                      : columns[columnIndex]?.highlighted;
                    return (
                      <td
                        key={columns[columnIndex]?.label ?? columnIndex}
                        className={cn(
                          "text-body border-current/10 border-b py-6",
                          // In compact (all-columns) tables, center text cells
                          // like the icon cells so "Limited" sits under its
                          // column header instead of hugging the left edge.
                          iconOnly || compact
                            ? "w-(--data-col) text-center align-middle"
                            : "pr-2 align-top md:pr-12",
                          columns[columnIndex]?.highlighted
                            ? "text-current"
                            : "text-current/60",
                          !compact &&
                            columnIndex !== visibleColumn &&
                            "hidden md:table-cell",
                        )}
                      >
                        <div
                          className={cn(
                            "flex gap-2",
                            iconOnly && "items-center",
                            (iconOnly || compact) && "justify-center",
                          )}
                        >
                          {showTick ? (
                            <RoundedCheck
                              aria-hidden
                              className={cn(
                                "text-voice-accent-green shrink-0",
                                iconOnly ? "size-6" : "mt-[0.15em] size-4",
                              )}
                            />
                          ) : (
                            <Close
                              aria-hidden
                              className={cn(
                                "text-current/30 shrink-0",
                                iconOnly ? "size-6" : "mt-[0.2em] size-3.5",
                              )}
                            />
                          )}
                          {iconOnly && (
                            <span className="md:text-body sr-only md:not-sr-only">
                              {showTick ? "Yes" : "No"}
                            </span>
                          )}
                          {!iconOnly && (
                            <div className={cn(!compact && "flex-1")}>
                              {value}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {initialVisibleRows != null && rows.length > initialVisibleRows && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => {
                  setExpanded((current) => !current);
                  if (expanded) {
                    triggerAnalyticsClick({
                      text: "View less",
                      analyticsId: viewLessMetadata.analyticsId,
                      location: ANALYTICS_REGION,
                    });
                  } else {
                    triggerAnalyticsClick({
                      text: "View more",
                      analyticsId: viewMoreMetadata.analyticsId,
                      location: ANALYTICS_REGION,
                    });
                  }
                }}
                className="text-label-lg border-current/20 hover:border-current/60 cursor-pointer border-b pb-1 transition-colors"
              >
                {expanded ? "View less" : `View all ${rows.length} comparisons`}
              </button>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
